// Enemies, bullets, gems, PSI combat. Ported from demon.c.

import { rand, RandomDir } from './rng';
import { G, rooms } from './state';
import { Enemy, Bullet, Diamond, RoomConnection } from './types';
import { IsSolid } from './tiles';
import { Get, GetRoom, Put } from './mapgen';
import { getImg, getSurfaceScale, getAssetScale, toGamePx } from '../render/assets';
import {
  TILE,
  SCREEN_W,
  SCREEN_H,
  SCREEN_CX,
  SCREEN_CY,
  S,
  PLAYERW,
  PLAYERH,
} from './constants';
import { isInrangeEnabled, isReticleEnabled } from './settings';
import { getActiveWorld } from './world';
import { clearDataEntities, hurtDataEntities } from './entities-runtime';
import {
  Surface,
  screen,
  blitGame,
  DrawCircle,
  DrawCircleEx,
  DrawRect,
  draw_text,
} from '../render/surface';
import { LoadingScreen } from './screens';
import { SND_Pos } from '../audio/index';
import { applySavedCreatures } from '../net/save';

const M_PI = Math.PI;
const tr = Math.trunc;

function sqr(x: number): number {
  return x * x;
}
function idist(x1: number, y1: number, x2: number, y2: number): number {
  return tr(Math.sqrt(sqr(x2 - x1) + sqr(y2 - y1)));
}

// --- lists ---------------------------------------------------------------
let enemy_stack: Enemy | null = null;
let bullet_stack: Bullet | null = null;
let active_stack: Enemy | null = null;
let gem_stack: Diamond | null = null;
const room_gems: (Diamond | null)[] = new Array(3000).fill(null);
const enemy_loc_stack: ({ e: Enemy; n: any } | null)[][] = Array.from({ length: 20 }, () =>
  new Array(20).fill(null)
);

const searched = new Int32Array(3000);
const searchdist = new Int32Array(3000);
let csearch = 0;
const room_active = new Int32Array(3000);
let max_activate_dist = 0;
let total_bullets = 0;
let active_enemies = 0;

const enemy_sprites: Surface[] = [];
let reticle: Surface;
let inrange: Surface;
let invis_enemy: Surface;
let teleflash: Surface | null = null;
let d_star_big: Surface | null = null;
let d_star_small: Surface | null = null;
let d_star_ls: Surface | null = null;
let gem_sprite: Surface | null = null;

// static locals converted to module scope
let killEnemy_lastkill = 0;
let enemySound_last = 0;
let enemySound_delay = 150;
let moveBullet_lastShieldHit = 0;

export function DestroyThings(): void {
  enemy_stack = null;
  bullet_stack = null;
  gem_stack = null;
  active_stack = null;
  for (let i = 0; i < 3000; i++) room_gems[i] = null;
  clearDataEntities(getActiveWorld());
}

function AddEnemyLoc(e: Enemy, x: number, y: number): void {
  x += 1;
  y += 1;
  enemy_loc_stack[x][y] = { e, n: enemy_loc_stack[x][y] };
}

function GetEnemyLoc(s_x: number, s_y: number) {
  return enemy_loc_stack[tr(s_x / S(1100)) + 1][tr(s_y / S(1100)) + 1];
}

function AddEnemyPos(e: Enemy): void {
  const x_loc = tr(e.x / S(1100));
  const y_loc = tr(e.y / S(1100));
  AddEnemyLoc(e, x_loc, y_loc);
  const ix = tr((e.x - SCREEN_CX) / S(1100));
  const iy = tr((e.y - SCREEN_CY) / S(1100));
  if (x_loc !== ix) AddEnemyLoc(e, ix, y_loc);
  if (y_loc !== iy) AddEnemyLoc(e, x_loc, iy);
  if (x_loc !== ix && y_loc !== iy) AddEnemyLoc(e, ix, iy);
}

// --- save/load (creature data) -------------------------------------------
export function serializeCreatures(): {
  total_enemies: number;
  killed_enemies: number;
  enemies: number[];
  gems: number[];
} {
  const enemies: number[] = [];
  let p = enemy_stack;
  while (p) {
    if (!p.delete_me) {
      enemies.push(p.x, p.y, p.room, p.enemy_type);
    }
    p = p.next;
  }
  const gems: number[] = [];
  let g = gem_stack;
  while (g) {
    if (!g.delete_me) gems.push(g.x, g.y, g.room, g.value);
    g = g.next;
  }
  return {
    total_enemies: G.total_enemies,
    killed_enemies: G.killed_enemies,
    enemies,
    gems,
  };
}

function ActivateVisited(): void {
  for (let i = 0; i < 3000; i++) {
    if (rooms[i].visited) ActivateEnemies(i);
    if (i % 10 === 9) LoadingScreen(4, i / 3000.0);
  }
  LoadingScreen(4, 1);
}

export function loadCreatures(
  data: {
    total_enemies: number;
    killed_enemies: number;
    enemies: number[];
    gems: number[];
  },
  coordScale = 1,
): void {
  G.total_enemies = data.total_enemies;
  G.killed_enemies = data.killed_enemies;
  const e = data.enemies;
  for (let i = 0; i < e.length; i += 4) {
    CreateEnemyEx(e[i] * coordScale, e[i + 1] * coordScale, e[i + 2], e[i + 3]);
    G.total_enemies--;
  }
  const gm = data.gems;
  for (let i = 0; i < gm.length; i += 4) {
    SCreateGem(gm[i] * coordScale, gm[i + 1] * coordScale, gm[i + 2], gm[i + 3]);
  }
  ActivateVisited();
}

function InitEnemySprites(): void {
  for (let i = 0; i < 10; i++) {
    enemy_sprites[i] = getImg(`dat/i/mons${i + 1}.png`);
  }
  reticle = getImg('dat/i/reticle.png');
  inrange = getImg('dat/i/inrange.png');
  invis_enemy = getImg('dat/i/hidden_monster.png');
}

export function CreateEnemy(enemy_x: number, enemy_y: number, enemy_room: number): Enemy {
  let enemy_type = rand() % (tr(rooms[enemy_room].s_dist / 5) + 1);
  if (rooms[enemy_room].room_type === 5) enemy_type += rand() % 3;
  if (enemy_type > 8) enemy_type = (rand() % 3) + 6;
  if (rooms[enemy_room].s_dist >= 15) {
    if (rand() % 64 === 0) enemy_type = 9;
  }
  return CreateEnemyEx(enemy_x, enemy_y, enemy_room, enemy_type);
}

export function CreateEnemyEx(
  enemy_x: number,
  enemy_y: number,
  enemy_room: number,
  enemy_type: number
): Enemy {
  const e: Enemy = {
    x: enemy_x,
    y: enemy_y,
    room: enemy_room,
    lives: 1,
    deaths: 0,
    str: 0,
    curr_follow: -1,
    followdepth: 4,
    enemy_type,
    image: enemy_sprites[0],
    blit_pos: 0,
    creationcost: 1,
    teleport_v: 0,
    speed: 1,
    t: rand() % 65536,
    active: 0,
    fire_rate: 1,
    last_room: -1,
    p_last_room: -1,
    move_dir: 0,
    min_gems: 0,
    max_gems: 0,
    next: null,
    next_active: null,
    delete_me: 0,
    dying: 0,
    m_exit: null,
  };
  rooms[enemy_room].enemies++;

  switch (enemy_type) {
    case 0:
      e.image = enemy_sprites[0]; e.lives = 1; e.str = 20; e.speed = 3; e.fire_rate = 20; e.min_gems = 0; e.max_gems = 3; e.creationcost = 1; break;
    case 1:
      e.image = enemy_sprites[1]; e.lives = 1; e.str = 50; e.speed = 4; e.fire_rate = 25; e.min_gems = 2; e.max_gems = 6; e.creationcost = 1; break;
    case 2:
      e.image = enemy_sprites[2]; e.lives = 1; e.str = 180; e.speed = 5; e.fire_rate = 40; e.min_gems = 8; e.max_gems = 15; e.creationcost = 1; break;
    case 3:
      e.image = enemy_sprites[3]; e.lives = 2; e.str = 220; e.speed = 3; e.fire_rate = 24; e.min_gems = 12; e.max_gems = 20; e.creationcost = 1; break;
    case 4:
      e.image = enemy_sprites[4]; e.lives = 1; e.str = 360; e.speed = 3; e.fire_rate = 32; e.min_gems = 18; e.max_gems = 32; e.creationcost = 2; break;
    case 5:
      e.image = enemy_sprites[5]; e.lives = 1; e.str = 450; e.speed = 3; e.fire_rate = 2; e.min_gems = 32; e.max_gems = 64; e.creationcost = 2; break;
    case 6:
      e.image = enemy_sprites[6]; e.lives = 2; e.str = 450; e.speed = 4; e.fire_rate = 10; e.min_gems = 50; e.max_gems = 100; e.creationcost = 2; break;
    case 7:
      e.image = enemy_sprites[7]; e.lives = 1; e.str = 500; e.speed = 4; e.fire_rate = 27; e.min_gems = 80; e.max_gems = 160; e.creationcost = 3; break;
    case 8:
      e.image = enemy_sprites[8]; e.lives = 4; e.str = 500; e.speed = 2; e.fire_rate = 8; e.min_gems = 200; e.max_gems = 400; e.creationcost = 4; break;
    case 9:
      e.image = enemy_sprites[0]; e.lives = 1; e.str = rooms[enemy_room].s_dist * 20; e.speed = 3; e.fire_rate = 21; e.min_gems = 300; e.max_gems = 600; e.creationcost = 3; break;
    case 10:
      e.image = enemy_sprites[9]; e.lives = 8; e.str = 500; e.speed = 1; e.fire_rate = 4; e.min_gems = 5000; e.max_gems = 6000; e.followdepth = 8; e.creationcost = 6; break;
  }

  if (G.training) {
    e.str = tr((e.str * 4) / 5);
    e.fire_rate += tr(e.fire_rate / 2);
    e.speed *= 2;
  }

  if (rooms[e.room].room_type === 5) {
    e.str = tr((e.str * 3) / 2);
    if (e.str > 1500) e.str = 1500;
    e.fire_rate = e.fire_rate - 1;
  }

  const frameH = tr(e.image.h / e.lives);
  e.blit_pos = (rand() % tr(e.image.w / frameH)) * frameH;
  e.next_active = null;
  e.next = enemy_stack;
  enemy_stack = e;
  AddEnemyPos(e);
  G.total_enemies++;
  return e;
}

function SCreateGem(x: number, y: number, r: number, v: number): void {
  if (IsSolid(Get(tr(x / TILE), tr(y / TILE)))) return;
  if (GetRoom(tr(x / TILE), tr(y / TILE)) !== r) return;
  if (v === 0) return;

  const g: Diamond = {
    x,
    y,
    room: r,
    t: rand() % 65536,
    next: gem_stack,
    next_in_room: room_gems[r],
    prv_in_room: null,
    value: v,
    delete_me: 0,
  };
  if (room_gems[r] !== null) room_gems[r]!.prv_in_room = g;
  gem_stack = g;
  room_gems[r] = g;
  G.total_gems++;
}

export function CreateGem(x: number, y: number, r: number, v: number): void {
  if (v === 0) return;
  if (rand() % 1000 < tr(Math.log(v) / 4) + (G.player_hp === 1 ? 5 : 0) + 2) {
    SCreateGem(x, y, r, 31337);
  } else {
    SCreateGem(x, y, r, v);
  }
}

export function PlayerDir(x: number, y: number): number {
  const dy = G.player_y + PLAYERH / 2 - y;
  const dx = G.player_x + PLAYERW / 2 - x;
  return Math.atan2(dy, dx);
}

export function PlayerDist(x: number, y: number): number {
  return tr(Math.sqrt(sqr(x - (G.player_x + PLAYERW / 2)) + sqr(y - (G.player_y + PLAYERH / 2))));
}

function CreateBullet(
  x: number,
  y: number,
  firer: Enemy | null,
  bullet_type: number,
  dir: number,
  spd: number
): Bullet {
  const b: Bullet = {
    x,
    y,
    firer,
    parent: null,
    room: firer !== null ? firer.room : GetRoom(tr(x / TILE), tr(y / TILE)),
    dying: 0,
    delete_me: 0,
    t: rand() % 65536,
    dir,
    speed: S(spd),
    invuln: 0,
    img: 0,
    natural_dir: 0,
    fire_time: 0,
    duration: 0,
    turn: 0,
    shield_damage: 0,
    next: null,
  };
  switch (bullet_type) {
    case 0: b.img = 0; break;
    case 1: b.img = 1; b.invuln = 1; break;
    case 2:
      b.img = 2; b.invuln = 1; b.t = 0; b.fire_time = 30; b.duration = 30; b.turn = 0.0; b.speed = 0.0; break;
    case 3: b.img = 3; break;
    case 4: b.img = 4; b.invuln = 1; break;
    case 5: b.img = 5; b.t = 0; break;
    case 6: b.img = 6; b.invuln = 1; break;
    case 7: b.img = 7; b.invuln = 1; break;
    case 8: b.img = 8; b.invuln = 1; break;
  }
  if (G.training) b.speed *= 0.8;
  b.next = bullet_stack;
  bullet_stack = b;
  total_bullets++;
  return b;
}

function FireLaser(
  x: number,
  y: number,
  firer: Enemy | null,
  dir: number,
  fire_time: number,
  duration: number,
  turn: number,
  dmg: number
): Bullet {
  const b = CreateBullet(x, y, firer, 2, dir, 0);
  b.fire_time = fire_time;
  b.duration = duration;
  b.turn = turn;
  b.shield_damage = dmg;
  if (G.training) {
    const f_total = b.fire_time + b.duration;
    if (b.duration > 1) {
      b.duration = tr(b.duration / 2);
      b.fire_time = f_total - b.duration;
    }
    b.shield_damage = tr((b.shield_damage + 1) / 2);
  }
  return b;
}

export function InitEnemies(): void {
  max_activate_dist = 0;
  InitEnemySprites();
  for (let ny = 0; ny < 20; ny++) for (let nx = 0; nx < 20; nx++) enemy_loc_stack[nx][ny] = null;

  G.total_enemies = 0;
  G.killed_enemies = 0;
  total_bullets = 0;
  active_enemies = 0;
  G.total_gems = 0;
  for (let i = 0; i < 3000; i++) room_active[i] = 0;

  if (G.game_load) {
    applySavedCreatures();
    if (G.current_boss > 0) SoupUpEnemies();
    return;
  }

  for (let c_room = 1; c_room < 3000; c_room++) {
    const cr_x = rooms[c_room].x + 1;
    const cr_y = rooms[c_room].y + 1;
    const cr_w = rooms[c_room].w - 2;
    const cr_h = rooms[c_room].h - 2;
    const room_size = cr_w * cr_h;

    let n_enemies = rand() % (tr(room_size / 4) + 1);
    if (rooms[c_room].room_type === 2) n_enemies = 0;
    if (rooms[c_room].room_type === 3) n_enemies += tr((n_enemies + room_size) / 2);
    if (rooms[c_room].room_type === 5) n_enemies = 50;

    while (n_enemies > 0) {
      let trying = 1;
      while (trying) {
        const e_x = cr_x * TILE + TILE + (rand() % (cr_w * TILE - TILE * 2 + 1));
        const e_y = cr_y * TILE + TILE + (rand() % (cr_h * TILE - TILE * 2 + 1));
        if (!IsSolid(Get(tr((e_x - S(16)) / TILE), tr((e_y - S(16)) / TILE))) && !IsSolid(Get(tr((e_x + S(16)) / TILE), tr((e_y - S(16)) / TILE)))) {
          if (!IsSolid(Get(tr((e_x - S(16)) / TILE), tr((e_y + S(16)) / TILE))) && !IsSolid(Get(tr((e_x + S(16)) / TILE), tr((e_y + S(16)) / TILE)))) {
            n_enemies -= CreateEnemy(e_x, e_y, c_room).creationcost;
            trying = 0;
          }
        }
      }
    }
    if (c_room % 100 === 99) LoadingScreen(2, c_room / 3000.0);
  }
}

function EnemyMovement(e: Enemy, move_x: number, move_y: number): number {
  if (!IsSolid(Get(tr((move_x - S(12)) / TILE), tr((move_y - S(12)) / TILE)))) {
    if (!IsSolid(Get(tr((move_x + S(12)) / TILE), tr((move_y - S(12)) / TILE)))) {
      if (!IsSolid(Get(tr((move_x - S(12)) / TILE), tr((move_y + S(12)) / TILE)))) {
        if (!IsSolid(Get(tr((move_x + S(12)) / TILE), tr((move_y + S(12)) / TILE)))) {
          e.x = move_x;
          e.y = move_y;
          return 1;
        }
      }
    }
  }
  return 0;
}

function ActivateSingleEnemy(t: Enemy): void {
  if (t.active) return;
  if (t.enemy_type !== 10) {
    if (t.enemy_type === 9) {
      if (max_activate_dist < 10) return;
    } else {
      if (t.enemy_type > tr(max_activate_dist / 5) + 2) return;
    }
  }
  t.next_active = active_stack;
  active_stack = t;
  active_enemies += 1;
  t.active = 1;
}

function XActivateSingleEnemy(t: Enemy): void {
  if (rooms[t.room].room_type === 2) return;
  if (rooms[t.room].room_type === 3) return;
  ActivateSingleEnemy(t);
}

function ZActivateEnemies(room: number): void {
  if (room_active[room]) return;
  room_active[room] = 1;

  let t = enemy_stack;
  while (t !== null) {
    if (rand() % 4 === 0) {
      if (t.room === room) {
        if (t.active === 0) XActivateSingleEnemy(t);
      }
    }
    t = t.next;
  }

  let rc = rooms[room].con;
  while (rc !== null) {
    if (rand() % 3 === 0) ZActivateEnemies(rc.c);
    rc = rc.n;
  }
}

export function ActivateEnemies(room: number): void {
  let t = enemy_stack;
  if (rooms[room].s_dist > max_activate_dist) max_activate_dist = rooms[room].s_dist;

  while (t !== null) {
    if (t.room === room) {
      if (t.active === 0) {
        ActivateSingleEnemy(t);
        if (rooms[room].room_type === 3) t.teleport_v = (rand() % 1500) + 50;
      }
    }
    t = t.next;
  }

  let rc = rooms[room].con;
  while (rc !== null) {
    if (rand() % 2 === 0) {
      if (rooms[rc.c].room_type !== 2 && rooms[rc.c].room_type !== 3) ZActivateEnemies(rc.c);
    }
    rc = rc.n;
  }
}

function CanEnterRoom(room: number): number {
  if (room === 0) return 0;
  if (rooms[room].room_type === 2) return 0;
  if (rooms[room].room_type === 3) return 0;
  if (rooms[room].room_type === 5) return 0;
  if (rooms[room].room_type === 6) return 0;
  if (G.artifacts[11]) {
    if (rooms[room].enemies > 3) return 0;
  }
  return 1;
}

function CanLeaveRoom(room: number): number {
  if (room === 0) return 0;
  if (rooms[room].room_type === 2) return 0;
  if (rooms[room].room_type === 3) return 0;
  if (rooms[room].room_type === 5) return 0;
  if (rooms[room].room_type === 6) return 0;
  return 1;
}

function KillEnemy(t: Enemy): void {
  if (t.dying > 0) return;
  if (t.teleport_v > 0) return;
  if (t.delete_me) return;
  const ct = performance.now();
  if (ct - killEnemy_lastkill > 100) {
    SND_Pos('dat/a/enemyhit.wav', 128, PlayerDist(t.x, t.y));
    killEnemy_lastkill = ct;
  }
  t.dying = 1;
}

function ArtifactRoomUnlock(room: number): void {
  let e = active_stack;
  while (e !== null) {
    if (e.delete_me === 0 && e.room === room) return;
    e = e.next_active;
  }

  for (let y = 0; y < rooms[room].h; y++) {
    for (let x = 0; x < rooms[room].w; x++) {
      const rx = x + rooms[room].x;
      const ry = y + rooms[room].y;
      const rt = Get(rx, ry);
      if (rt >= 21 && rt <= 24) Put(rx, ry, rt - 21 + 13, room);
    }
  }

  let placed = 0;
  const tot_treasures = 2 + (rand() % (tr(rooms[room].s_dist / 8) + 1));
  while (placed < tot_treasures) {
    const x = rooms[room].x + (rand() % (rooms[room].w - 2));
    const y = rooms[room].y + (rand() % (rooms[room].h - 2));
    if ((x + y) % 2 === (placed > 0 ? 1 : 0)) {
      if (!IsSolid(Get(x, y))) {
        Put(x, y, 26, room);
        placed++;
      }
    }
  }
  rooms[room].room_type = 4;
}

export function EnemySound(t: number, dist: number): void {
  const curr = performance.now();
  if (curr - enemySound_delay < enemySound_last) return;
  switch (t) {
    case 0: SND_Pos('dat/a/mons0shot.wav', 48, dist); enemySound_delay = 200; break;
    case 1: SND_Pos('dat/a/mons1shot.wav', 112, dist); enemySound_delay = 500; break;
    case 2: SND_Pos('dat/a/mons2shot.wav', 110, dist); enemySound_delay = 1000; break;
    case 3: SND_Pos('dat/a/mons3shot.wav', 110, dist); enemySound_delay = 500; break;
    case 4: SND_Pos('dat/a/mons4shot.wav', 110, dist); enemySound_delay = 900; break;
    case 5: SND_Pos('dat/a/mons5shot.wav', 80, dist); enemySound_delay = 60; break;
    case 6: SND_Pos('dat/a/mons6shot.wav', 110, dist); enemySound_delay = 1000; break;
    case 7: SND_Pos('dat/a/mons7shot.wav', 110, dist); enemySound_delay = 600; break;
    case 8: SND_Pos('dat/a/mons8shot.wav', 110, dist); enemySound_delay = 700; break;
    case 9: SND_Pos('dat/a/mons9shot.wav', 110, dist); enemySound_delay = 242; break;
    case 10: SND_Pos('dat/a/mons10shot.wav', 110, dist); enemySound_delay = 250; break;
  }
  enemySound_last = curr;
}

function MoveEnemy(e: Enemy): void {
  e.t++;
  if (e.teleport_v > 0) {
    e.teleport_v--;
    return;
  }

  const enemy_fire_type = e.enemy_type < 10 ? e.enemy_type : rand() % 10;

  if (e.dying === 0) {
    if (e.t % e.fire_rate === 0) {
      if (e.room === G.player_room) {
        const ex = e.x;
        const ey = e.y;
        const pd = () => PlayerDir(ex, ey);
        switch (enemy_fire_type) {
          case 0:
            EnemySound(0, PlayerDist(ex, ey));
            CreateBullet(ex, ey, e, 0, pd(), 1.5);
            if (e.enemy_type === 10) e.fire_rate = 20;
            break;
          case 1:
            EnemySound(1, PlayerDist(ex, ey));
            CreateBullet(ex, ey, e, 0, pd() + 0.1, 2.1);
            CreateBullet(ex, ey, e, 3, pd(), 2.4);
            CreateBullet(ex, ey, e, 3, pd() - 0.1, 2.1);
            CreateBullet(ex, ey, e, 0, pd(), 1.7);
            if (e.enemy_type === 10) e.fire_rate = 25;
            break;
          case 2: {
            EnemySound(2, PlayerDist(ex, ey));
            for (let dp = 0; dp < M_PI * 2; dp += 0.25) CreateBullet(ex, ey, e, 0, pd() + dp, 1.1);
            const d0 = RandomDir();
            for (let i = 0; i < 20; i++) CreateBullet(ex, ey, e, 0, d0, i * 0.1 + 2.5);
            if (e.enemy_type === 10) e.fire_rate = 40;
            break;
          }
          case 3:
            EnemySound(3, PlayerDist(ex, ey));
            CreateBullet(ex, ey, e, 0, pd(), 2.5);
            CreateBullet(ex, ey, e, 0, pd() + 0.1, 2.5);
            CreateBullet(ex, ey, e, 0, pd() + 0.2, 2.5);
            CreateBullet(ex, ey, e, 1, pd(), 1.5);
            CreateBullet(ex, ey, e, 0, pd() - 0.2, 2.2);
            CreateBullet(ex, ey, e, 0, pd() - 0.3, 2.2);
            if (e.enemy_type === 10) e.fire_rate = 24;
            break;
          case 4:
            EnemySound(4, PlayerDist(ex, ey));
            for (let dp = 0; dp < M_PI * 0.66; dp += 0.2) {
              CreateBullet(ex, ey, e, 0, pd() + 0.5 - dp, 1.8 + dp / 2);
              CreateBullet(ex, ey, e, 0, pd() - 0.4 + dp, 1.8 + dp / 2);
            }
            FireLaser(ex, ey, e, pd(), 24, 4, 20.0, 6);
            if (e.enemy_type === 10) e.fire_rate = 32;
            break;
          case 5:
            EnemySound(5, PlayerDist(ex, ey));
            CreateBullet(ex, ey, e, 0, pd(), 4);
            if (e.enemy_type === 10) e.fire_rate = 2;
            break;
          case 6:
            EnemySound(6, PlayerDist(ex, ey));
            CreateBullet(ex, ey, e, 0, pd() + 0.25, 6);
            CreateBullet(ex, ey, e, 0, pd() + 0.25, 4);
            CreateBullet(ex, ey, e, 5, pd() - 0.05, 4.99);
            CreateBullet(ex, ey, e, 5, pd(), 4.99);
            CreateBullet(ex, ey, e, 5, pd() + 0.05, 4.99);
            CreateBullet(ex, ey, e, 0, pd() - 0.25, 5);
            CreateBullet(ex, ey, e, 0, pd() - 0.25, 3);
            if (e.enemy_type === 10) e.fire_rate = 10;
            break;
          case 7:
            EnemySound(7, PlayerDist(ex, ey));
            for (let dp = 0; dp < M_PI * 0.66; dp += 0.1) {
              CreateBullet(ex, ey, e, 0, pd() + 0.5 - dp, 1.8 + dp / 2);
              CreateBullet(ex, ey, e, 3, pd() - 0.4 + dp, 1.8 + dp / 2);
              CreateBullet(ex, ey, e, 3, pd() + 0.5 - dp * 2, 4 + dp * 2);
              CreateBullet(ex, ey, e, 0, pd() - 0.4 + dp * 2, 4 + dp * 2);
            }
            FireLaser(ex, ey, e, e.t / 25.0, 16, 10, 0.08, 4);
            FireLaser(ex, ey, e, e.t / 25.0 + (M_PI * 2) / 4, 16, 10, 0.08, 4);
            FireLaser(ex, ey, e, e.t / 25.0 + (M_PI * 4) / 4, 16, 10, 0.08, 4);
            FireLaser(ex, ey, e, e.t / 25.0 + (M_PI * 6) / 4, 16, 10, 0.08, 4);
            if (e.enemy_type === 10) e.fire_rate = 27;
            break;
          case 8:
            EnemySound(8, PlayerDist(ex, ey));
            for (let dp = 0; dp < 1; dp += 0.1) {
              CreateBullet(ex, ey, e, 0, pd() + M_PI / 2 + dp, 3 + dp / 2);
              CreateBullet(ex, ey, e, 0, pd(), 4 + dp);
              CreateBullet(ex, ey, e, 0, pd() - M_PI / 2 - dp, 3 + dp / 2);
            }
            CreateBullet(ex, ey, e, 6, tr(e.t / e.fire_rate) * 0.7, 2);
            if (e.enemy_type === 10) e.fire_rate = 8;
            break;
          case 9: {
            const i = rand() % ((rand() % (PlayerDist(ex, ey) + 1)) + 1);
            if (i < 15) {
              EnemySound(9, PlayerDist(ex, ey));
              const dpf = (2000 - e.str) / 2500.0;
              for (let dp = 0; dp < 1; dp += dpf) {
                CreateBullet(ex, ey, e, 0, pd() + 0.1 - dp / 5.0, 3.5 + dp / 3.0);
                CreateBullet(ex, ey, e, 0, pd() + (2 * M_PI) / 3 + 0.1 - dp / 5.0, 3.5 + dp / 3.0);
                CreateBullet(ex, ey, e, 0, pd() + (4 * M_PI) / 3 + 0.1 - dp / 5.0, 3.5 + dp / 3.0);
                CreateBullet(ex, ey, e, 4, pd() + 0.1 - dp / 5.0, 7 + dp * 5);
                CreateBullet(ex, ey, e, 4, pd() + (2 * M_PI) / 3 + 0.1 - dp / 5.0, 7 + dp * 5);
                CreateBullet(ex, ey, e, 4, pd() + (4 * M_PI) / 3 + 0.1 - dp / 5.0, 7 + dp * 5);
              }
            }
            if (e.enemy_type === 10) e.fire_rate = 21;
            break;
          }
        }
      }
    }

    if (e.t % e.speed === 0) {
      if (G.player_room === e.room) {
        e.m_exit = null;
        if (e.t % (e.speed * (8 + (rand() % 6))) === 0) {
          e.move_dir = ((rand() % 256) / 256.0) * M_PI * 2.0;
        }
        const move_x = e.x + tr(Math.cos(e.move_dir) * S(5));
        const move_y = e.y + tr(Math.sin(e.move_dir) * S(5));
        EnemyMovement(e, move_x, move_y);
      } else {
        if (CanLeaveRoom(e.room)) {
          let door_x = 0;
          let door_y = 0;
          let can_move = 0;
          let rcon: RoomConnection | null = null;

          if (e.m_exit !== null) {
            rcon = e.m_exit;
            door_x = (rcon.x + (rcon.x - rcon.x2)) * TILE + S(16);
            door_y = (rcon.y + (rcon.y - rcon.y2)) * TILE + S(16);
            can_move = 1;
          } else {
            let con_traverse = rooms[e.room].con;
            let nearest = PlayerDist(e.x, e.y);
            rcon = null;
            while (con_traverse !== null) {
              const i = PlayerDist(con_traverse.x2 * TILE + S(16), con_traverse.y2 * TILE + S(16));
              if (i < nearest && CanEnterRoom(GetRoom(con_traverse.x2, con_traverse.y2))) {
                nearest = i;
                rcon = con_traverse;
              }
              con_traverse = con_traverse.n;
            }
            if (rcon !== null) {
              door_x = (rcon.x + (rcon.x - rcon.x2)) * TILE + S(16);
              door_y = (rcon.y + (rcon.y - rcon.y2)) * TILE + S(16);
              e.m_exit = rcon;
              can_move = 1;
            }
          }

          if (can_move && rcon !== null) {
            if (Math.abs(door_x - e.x) + Math.abs(door_y - e.y) < S(6)) {
              e.last_room = e.room;
              e.p_last_room = G.player_room;
              e.x = (rcon.x2 + (rcon.x2 - rcon.x)) * TILE + S(16);
              e.y = (rcon.y2 + (rcon.y2 - rcon.y)) * TILE + S(16);
              rooms[e.room].enemies--;
              e.room = rcon.c;
              rooms[e.room].enemies++;
              e.curr_follow = -1;
              e.m_exit = null;
            } else {
              e.move_dir = Math.atan2(door_y - e.y, door_x - e.x);
              const move_x = e.x + tr(Math.cos(e.move_dir) * S(5));
              const move_y = e.y + tr(Math.sin(e.move_dir) * S(5));
              EnemyMovement(e, move_x, move_y);
            }
          }
        }
      }
    }
  }

  if (e.dying > 0) {
    e.dying++;
    if (e.dying === 3) {
      let b = bullet_stack;
      while (b !== null) {
        if (!b.delete_me) {
          if (b.firer === e) {
            b.delete_me = 1;
            CreateGem(b.x, b.y, b.room, tr((e.max_gems + e.min_gems) / 5) + (G.artifacts[2] * tr((e.max_gems + e.min_gems) / 4)));
          }
        }
        b = b.next;
      }
    }
    if (e.dying >= 20) {
      let actual_lives: number;
      if (e.lives >= 4 && G.training) actual_lives = tr((e.lives * 3) / 4);
      else actual_lives = e.lives;

      e.deaths++;
      if (e.deaths >= actual_lives) {
        e.deaths--;
        e.delete_me = 1;
        e.dying = 0;
        G.killed_enemies++;
        rooms[e.room].enemies--;
        const n_gems = e.min_gems + (rand() % (e.max_gems - e.min_gems + 1));
        for (let i = 0; i < n_gems; i++) {
          CreateGem(e.x - S(16) + (rand() % S(32)), e.y - S(16) + (rand() % S(32)), e.room, 1 + (rand() % 4) + G.artifacts[2] * (rand() % 3));
        }
        if (rooms[e.room].room_type === 3) ArtifactRoomUnlock(e.room);
      } else {
        e.dying = 0;
      }
    }
  }
}

function MoveBullet(e: Bullet): void {
  e.t++;
  if (G.boss_fight_mode !== 0 && G.boss_fight_mode !== 2 && e.dying === 0) {
    e.dying = 1;
  }

  if (e.dying === 0) {
    if (e.img === 7) {
      if (G.proxy_seek) {
        e.speed = S(10);
        e.img = 4;
        e.dir = (e.dir + PlayerDir(e.x, e.y)) / 2;
      }
    }

    e.x += Math.cos(e.dir) * e.speed;
    e.y += Math.sin(e.dir) * e.speed;

    if (e.img === 1) {
      if (e.t % 20 === 19) {
        if (e.t % 40 >= 20) CreateBullet(e.x, e.y, e.firer, 3, e.dir + M_PI / 2, e.speed * 0.75);
        else CreateBullet(e.x, e.y, e.firer, 3, e.dir - M_PI / 2, e.speed * 0.75);
      }
    }
    if (e.img === 6) {
      if (e.t % 40 === 39) {
        FireLaser(e.x, e.y, e.firer, e.t / 30.0, 20, 10, 0.05, 2).parent = e;
        FireLaser(e.x, e.y, e.firer, e.t / 30.0 + (M_PI * 2) / 3, 20, 10, 0.05, 2).parent = e;
        FireLaser(e.x, e.y, e.firer, e.t / 30.0 + (M_PI * 4) / 3, 20, 10, 0.05, 2).parent = e;
      }
    }

    if (e.img === 5) {
      if (e.t < 100) {
        if (e.t % 20 === 9) {
          const n = CreateBullet(e.x, e.y, e.firer, 5, e.dir + M_PI / 4, e.speed);
          n.natural_dir = e.dir;
          n.t = e.t;
          e.natural_dir = e.dir;
          e.dir -= M_PI / 4;
        }
        if (e.t % 20 === 19) e.dir = e.natural_dir;
      }
      if (e.t === 100) {
        e.dir = PlayerDir(e.x, e.y);
        e.speed *= 1.5;
      }
    }

    if (IsSolid(Get(tr(e.x / TILE), tr(e.y / TILE)))) {
      if (e.img === 3) {
        if (e.room === G.player_room) {
          e.dir = PlayerDir(e.x, e.y);
          if (IsSolid(Get(tr((e.x + Math.cos(e.dir) * e.speed * 1.5) / TILE), tr((e.y + Math.sin(e.dir) * e.speed * 1.5) / TILE)))) e.dying = 1;
        } else {
          e.dying = 1;
        }
      } else {
        e.dying = 1;
      }
    }

    if (e.img === 2) {
      if (e.parent !== null) {
        e.x = e.parent.x;
        e.y = e.parent.y;
      } else if (e.firer !== null) {
        e.x = e.firer.x;
        e.y = e.firer.y;
      }
      if (e.dying === 0) {
        if (e.t > e.fire_time && e.t <= e.fire_time + e.duration) {
          let fx = e.x;
          let fy = e.y;
          while (!IsSolid(Get(tr(fx / TILE), tr(fy / TILE)))) {
            if (G.player_dying === 0) {
              if (PlayerDist(fx, fy) < S(30)) {
                if (G.player_shield > 0 && G.shield_hp > 0) {
                  G.shield_hp -= e.shield_damage;
                  if (G.shield_hp >= 0) {
                    e.dying = 1;
                    break;
                  } else {
                    G.shield_hp = 0;
                  }
                }
                if (PlayerDist(fx, fy) < S(4) - S(2) * G.artifacts[5]) {
                  G.player_dying = 1;
                  SND_Pos('dat/a/playerhurt.wav', 128, 0);
                  e.dying = 1;
                  break;
                }
              }
            }
            fx += Math.cos(e.dir) * S(2);
            fy += Math.sin(e.dir) * S(2);
          }
        }
        if (e.turn > 10) {
          if (e.dir > PlayerDir(e.x, e.y)) {
            e.dir -= (e.dir - PlayerDir(e.x, e.y)) / (e.turn - 9.0);
            e.dir -= (e.dir - PlayerDir(e.x, e.y)) / (e.turn - 9.0);
          } else {
            e.dir += (PlayerDir(e.x, e.y) - e.dir) / (e.turn - 9.0);
          }
        } else {
          e.dir += e.turn;
        }
        if (e.t > e.fire_time + e.duration) e.dying = 1;
      }
    } else {
      const pdist = PlayerDist(e.x, e.y);
      if (pdist < S(30)) {
        if (G.player_dying === 0) {
          if (G.player_shield > 0) {
            if (G.shield_hp > 0) {
              G.shield_hp--;
              if (e.img === 4) {
                e.dying = 1;
              } else {
                e.dir += M_PI;
                const c = performance.now();
                if (c - 150 > moveBullet_lastShieldHit) {
                  SND_Pos('dat/a/shieldhit.wav', 50, 0);
                  moveBullet_lastShieldHit = c;
                }
                while (PlayerDist(e.x, e.y) < S(30)) {
                  e.x += Math.cos(e.dir) * e.speed;
                  e.y += Math.sin(e.dir) * e.speed;
                }
              }
            }
          }
        }
      }
      if (e.dying === 0) {
        if (pdist < S(6) - S(2) * G.artifacts[5]) {
          if (G.player_dying === 0) {
            SND_Pos('dat/a/playerhurt.wav', 128, 0);
            G.player_dying = 1;
          }
        }
      }
    }
  }

  if (e.dying === 1 && e.img === 8) {
    SpawnLaser(e.x - Math.cos(e.dir) * e.speed, e.y - Math.sin(e.dir) * e.speed, PlayerDir(e.x, e.y), 10, 10, 0.0, tr(G.player_shield / 6));
  }

  if (GetRoom(tr(e.x / TILE), tr(e.y / TILE)) !== e.room) e.delete_me = 1;

  if (e.dying > 0) {
    e.dying++;
    if (e.dying >= 10) {
      e.delete_me = 1;
      e.dying = 0;
    }
  }
}

// --- drawing -------------------------------------------------------------

function DrawEnemy(e: Enemy): void {
  if (e.delete_me) return;
  const ns = getSurfaceScale(e.image);
  const frameHAsset = tr(e.image.h / e.lives);
  const frameH = tr(frameHAsset / ns);
  let draw_x = e.x - tr(frameH / 2) - G.scroll_x;
  let draw_y = e.y - tr(frameH / 2) - G.scroll_y;

  let sx = e.blit_pos;
  let sy = frameHAsset * e.deaths;
  let sw = frameHAsset;
  let sh = frameHAsset;

  if (e.teleport_v < 8) {
    if (e.dying === 0) {
      blitGame(e.image, { x: sx, y: sy, w: sw, h: sh }, screen, draw_x, draw_y, ns);
    } else {
      if (e.deaths + 1 >= e.lives) {
        sw = tr((frameHAsset * (20 - e.dying)) / 20);
      } else {
        sw = tr((frameHAsset * (20 - tr(e.dying / 2))) / 20);
      }
      sh = sw;
      sx += tr((frameHAsset - sw) / 2);
      sy += tr((frameHAsset - sw) / 2);
      const shrinkGame = tr(toGamePx(sw, ns));
      draw_x += tr((frameH - shrinkGame) / 2);
      draw_y += tr((frameH - shrinkGame) / 2);
      blitGame(e.image, { x: sx, y: sy, w: sw, h: sh }, screen, draw_x, draw_y, ns);
    }

    if (e.t % 8 === 1 && !G.game_paused) {
      e.blit_pos = (e.blit_pos + frameHAsset) % e.image.w;
    }

    const reticleNs = getSurfaceScale(reticle);
    let rx = e.x - tr(frameH / 2) - G.scroll_x - tr((S(128) - frameH) / 2);
    let ry = e.y - tr(frameH / 2) - G.scroll_y - tr((S(128) - frameH) / 2);
    if (isReticleEnabled() && G.magic_circuit >= e.str) blitGame(reticle, null, screen, rx, ry, reticleNs);

    rx = e.x - tr(frameH / 2) - G.scroll_x - tr((S(128) - frameH) / 2);
    ry = e.y - tr(frameH / 2) - G.scroll_y - tr((S(128) - frameH) / 2);
    if (
      isInrangeEnabled() &&
      Math.sqrt(sqr(e.x - G.player_x) + sqr(e.y - G.player_y)) < G.circuit_range
    ) {
      blitGame(inrange, null, screen, rx, ry, getSurfaceScale(inrange));
    }
  }

  if (e.teleport_v < 24) {
    if (teleflash === null) teleflash = getImg('dat/i/teleflash.png');
    const tfNs = getSurfaceScale(teleflash);
    const tfFrame = 48 * tfNs;
    const fx = tfFrame * tr(e.teleport_v / 3);
    blitGame(
      teleflash,
      { x: fx, y: 0, w: tfFrame, h: tfFrame },
      screen,
      e.x - S(24) - G.scroll_x,
      e.y - S(24) - G.scroll_y,
      tfNs
    );
  }
}

function DrawBullet(b: Bullet): void {
  if (b.delete_me) return;
  const sx = G.scroll_x;
  const sy = G.scroll_y;
  if (b.img === 0) {
    if (b.dying > 0) {
      DrawCircle(b.x - sx, b.y - sy, S(6) + tr(b.dying / 2), b.dying * 10);
      DrawCircle(b.x - sx, b.y - sy, S(3) + tr(b.dying / 2), 255 - b.dying * 10);
    } else if (b.t % 8 < 4) {
      DrawCircle(b.x - sx, b.y - sy, S(6), 0);
      DrawCircle(b.x - sx, b.y - sy, S(4), 255);
    } else {
      DrawCircle(b.x - sx, b.y - sy, S(6), 255);
      DrawCircle(b.x - sx, b.y - sy, S(4), 0);
    }
  }
  if (b.img === 1) {
    if (d_star_big === null) d_star_big = getImg('dat/i/star1.png');
    if (b.dying === 0) {
      const starNs = getSurfaceScale(d_star_big);
      const cell = 32 * starNs;
      blitGame(d_star_big, { x: (b.t % 8) * cell, y: 0, w: cell, h: cell }, screen, b.x - S(16) - sx, b.y - S(16) - sy, starNs);
    } else {
      DrawCircle(b.x - sx, b.y - sy, S(4) + b.dying, b.dying * 10);
      DrawCircle(b.x - sx, b.y - sy, S(2) + b.dying, 255 - b.dying * 10);
    }
  }
  if (b.img === 2) {
    if (b.t <= b.fire_time) {
      const z = tr((b.t * 150) / (b.fire_time + 1)) + 80;
      let fx = b.x;
      let fy = b.y;
      while (!IsSolid(Get(tr(fx / TILE), tr(fy / TILE)))) {
        DrawRect(fx - S(1) - sx, fy - S(1) - sy, S(4), S(4), tr(z / 2));
        fx += Math.cos(b.dir) * S(2);
        fy += Math.sin(b.dir) * S(2);
        if (G.player_shield > 0 && G.shield_hp > 0 && PlayerDist(fx, fy) < S(30)) break;
        if (PlayerDist(fx, fy) < S(4)) break;
      }
      DrawCircle(b.x - sx, b.y - sy, S(5), (z + 255) / 2.4);
      DrawCircle(fx - Math.cos(b.dir) * S(2) - sx, fy - Math.sin(b.dir) * S(2) - sy, S(4), tr(z / 2));
      fx = b.x;
      fy = b.y;
      while (!IsSolid(Get(tr(fx / TILE), tr(fy / TILE)))) {
        DrawRect(fx - sx, fy - sy, S(2), S(2), z);
        fx += Math.cos(b.dir) * S(2);
        fy += Math.sin(b.dir) * S(2);
        if (G.player_shield > 0 && G.shield_hp > 0 && PlayerDist(fx, fy) < S(30)) break;
        if (PlayerDist(fx, fy) < S(4)) break;
      }
      DrawCircle(b.x - sx, b.y - sy, S(3), (z + 255) / 2);
      DrawCircle(fx - Math.cos(b.dir) * S(2) - sx, fy - Math.sin(b.dir) * S(2) - sy, S(2), z);
    }
    if (b.t > b.fire_time && b.t <= b.fire_time + b.duration) {
      const z = 255 - tr(((b.t - b.fire_time) * 200) / (b.duration + 1));
      let fx = b.x;
      let fy = b.y;
      while (!IsSolid(Get(tr(fx / TILE), tr(fy / TILE)))) {
        DrawRect(fx - S(3) - sx, fy - S(3) - sy, S(8), S(8), tr((z * 2) / 3));
        fx += Math.cos(b.dir) * S(2);
        fy += Math.sin(b.dir) * S(2);
        if (G.player_shield > 0 && G.shield_hp > 0 && PlayerDist(fx, fy) < S(30)) break;
        if (PlayerDist(fx, fy) < S(4)) break;
      }
      DrawCircle(b.x - sx, b.y - sy, S(9), (z + 255) / 2.4);
      DrawCircle(fx - Math.cos(b.dir) * S(2) - sx, fy - Math.sin(b.dir) * S(2) - sy, S(8), tr((z * 2) / 3));
      fx = b.x;
      fy = b.y;
      while (!IsSolid(Get(tr(fx / TILE), tr(fy / TILE)))) {
        DrawRect(fx - S(2) - sx, fy - S(2) - sy, S(6), S(6), z);
        fx += Math.cos(b.dir) * S(2);
        fy += Math.sin(b.dir) * S(2);
        const x1 = fx + (rand() % S(12)) - (rand() % S(12)) - sx;
        const y1 = fy + (rand() % S(12)) - (rand() % S(12)) - sy;
        DrawRect(x1, y1, S(2), S(2), rand() % (z + 1));
        if (G.player_shield > 0 && G.shield_hp > 0 && PlayerDist(fx, fy) < S(30)) break;
        if (PlayerDist(fx, fy) < S(4)) break;
      }
      DrawCircle(b.x - sx, b.y - sy, S(7), (z + 255) / 2);
      DrawCircle(fx - Math.cos(b.dir) * S(2) - sx, fy - Math.sin(b.dir) * S(2) - sy, S(6), z);
    }
  }
  if (b.img === 3) {
    if (d_star_small === null) d_star_small = getImg('dat/i/star2.png');
    if (b.dying === 0) {
      const starNs = getSurfaceScale(d_star_small);
      const cell = 16 * starNs;
      blitGame(d_star_small, { x: (b.t % 8) * cell, y: 0, w: cell, h: cell }, screen, b.x - S(8) - sx, b.y - S(8) - sy, starNs);
    } else {
      DrawCircle(b.x - sx, b.y - sy, S(4) + tr(b.dying / 2), b.dying * 10);
      DrawCircle(b.x - sx, b.y - sy, S(2) + tr(b.dying / 2), 255 - b.dying * 10);
    }
  }
  if (b.img === 4) {
    if (b.dying === 0) {
      const x1 = b.x + Math.cos(b.dir) * S(2) - sx;
      const y1 = b.y + Math.sin(b.dir) * S(2) - sy;
      const x2 = b.x - Math.cos(b.dir) * S(80) - sx;
      const y2 = b.y - Math.sin(b.dir) * S(80) - sy;
      for (let i = 0; i < 40; i++) {
        const xo1 = tr(((x2 - x1) * i) / 40 + x1);
        const yo1 = tr(((y2 - y1) * i) / 40 + y1);
        let z = tr(i / 2);
        if (i > 10) z = tr((40 - i) / 7);
        DrawRect(xo1 - z - S(2), yo1 - z - S(2), z * 2 + S(4), z * 2 + S(4), 200);
      }
      for (let i = 0; i < 40; i++) {
        const xo1 = tr(((x2 - x1) * i) / 40 + x1);
        const yo1 = tr(((y2 - y1) * i) / 40 + y1);
        let z = tr(i / 2);
        if (i > 10) z = tr((40 - i) / 7);
        DrawRect(xo1 - z, yo1 - z, z * 2, z * 2, 255);
      }
    } else {
      DrawCircleEx(b.x - sx, b.y - sy, b.dying * 2 + 1, b.dying * 1.75 - 1, 230);
      DrawCircleEx(b.x - sx, b.y - sy, b.dying * 2, b.dying * 1.75, 255);
    }
  }
  if (b.img === 5) {
    if (b.dying > 0) {
      let i = S(4) + tr(b.dying / 5);
      DrawRect(b.x - sx - i, b.y - sy - i, i, i, b.dying * 10);
      i = S(2) + tr(b.dying / 5);
      DrawRect(b.x - sx - i, b.y - sy - i, i, i, 255 - b.dying * 10);
    } else if (b.t % 8 < 4) {
      DrawRect(b.x - sx - S(1), b.y - sy - S(1), S(4), S(4), 0);
      DrawRect(b.x - sx, b.y - sy, S(2), S(2), 255);
    } else {
      DrawRect(b.x - sx - S(1), b.y - sy - S(1), S(4), S(4), 255);
      DrawRect(b.x - sx, b.y - sy, S(2), S(2), 0);
    }
  }
  if (b.img === 6) {
    if (d_star_ls === null) d_star_ls = getImg('dat/i/star3.png');
    if (b.dying === 0) {
      const starNs = getSurfaceScale(d_star_ls);
      const cell = 32 * starNs;
      blitGame(d_star_ls, { x: (b.t % 8) * cell, y: 0, w: cell, h: cell }, screen, b.x - S(16) - sx, b.y - S(16) - sy, starNs);
    } else {
      DrawCircle(b.x - sx, b.y - sy, S(4) + b.dying, 255 - b.dying * 10);
      DrawCircle(b.x - sx, b.y - sy, S(2) + b.dying, b.dying * 10);
    }
  }
  if (b.img === 7) {
    if (b.t % 8 < 4) {
      DrawCircle(b.x - sx, b.y - sy, S(8), 0);
      DrawCircle(b.x - sx, b.y - sy, S(5), 255);
    } else {
      DrawCircle(b.x - sx, b.y - sy, S(8), 255);
      DrawCircle(b.x - sx, b.y - sy, S(5), 0);
    }
  }
  if (b.img === 8) {
    if (b.dying > 0) {
      DrawCircle(b.x - sx, b.y - sy, S(12) + b.dying, b.dying * 10);
      DrawCircle(b.x - sx, b.y - sy, S(6) + b.dying, 255 - b.dying * 10);
    } else if (b.t % 6 < 3) {
      DrawCircle(b.x - sx, b.y - sy, S(12), 0);
      DrawCircle(b.x - sx, b.y - sy, S(8), 255);
    } else {
      DrawCircle(b.x - sx, b.y - sy, S(12), 255);
      DrawCircle(b.x - sx, b.y - sy, S(8), 0);
    }
  }
}

function DrawGem(g: Diamond): void {
  if (g.delete_me) return;
  if (gem_sprite === null) gem_sprite = getImg('dat/i/gem.png');
  let gemtype = 0;
  if (g.value === 1) gemtype = 2;
  if (g.value > 1 && g.value < 5) gemtype = 1;
  if (g.value >= 5) gemtype = 0;

  const fxp = tr(performance.now() / 300) % 2 ? 255 : 0;

  if (g.value !== 31337) {
    const gemNs = getSurfaceScale(gem_sprite);
    const cell = 8 * gemNs;
    const typeStep = 32 * gemNs;
    blitGame(
      gem_sprite,
      { x: (g.t % 4) * cell + typeStep * gemtype, y: 0, w: cell, h: cell },
      screen,
      g.x - S(4) - G.scroll_x,
      g.y - S(4) - G.scroll_y,
      gemNs
    );
  } else {
    DrawCircle(g.x - G.scroll_x, g.y - G.scroll_y, S(6), (rand() % 64) ^ fxp);
    draw_text(g.x - S(4) - G.scroll_x, g.y - S(4) - G.scroll_y, String.fromCharCode(3), (200 + (rand() % 56)) ^ fxp);
  }
  g.t++;
}

function DrawInvisible(x: number, y: number): void {
  blitGame(invis_enemy, null, screen, x - G.scroll_x - S(24), y - G.scroll_y - S(24), getSurfaceScale(invis_enemy));
}

export function DrawEntities(): void {
  const pr = G.player_room;
  if (rooms[pr].room_type !== 3 && rooms[pr].room_type !== 2) {
    let g = room_gems[pr];
    while (g !== null) {
      if (g.room === pr && g.delete_me === 0 && g.value !== 31337) DrawGem(g);
      g = g.next_in_room;
    }
    g = room_gems[pr];
    while (g !== null) {
      if (g.room === pr && g.delete_me === 0 && g.value === 31337) DrawGem(g);
      g = g.next_in_room;
    }
  }

  let t = active_stack;
  while (t !== null) {
    if (!t.delete_me) {
      if (t.room === pr && t.enemy_type !== 9) DrawEnemy(t);
    }
    t = t.next_active;
  }

  if (G.artifacts[6]) {
    t = active_stack;
    while (t !== null) {
      if (!t.delete_me) {
        if (t.room !== pr || t.enemy_type === 9) DrawInvisible(t.x, t.y);
      }
      t = t.next_active;
    }
    if (!G.artifacts[11]) {
      let els = GetEnemyLoc(G.scroll_x, G.scroll_y);
      while (els !== null) {
        const te = els.e;
        if (!te.delete_me && (te.room !== pr || te.enemy_type === 9) && te.active === 0) {
          if (te.x + S(24) >= G.scroll_x && te.y + S(24) >= G.scroll_y) {
            if (te.x - S(24) <= G.scroll_x + SCREEN_W - 1) {
              if (te.y - S(24) <= G.scroll_y + SCREEN_H - 1) {
                if (rooms[te.room].room_type !== 2 && rooms[te.room].room_type !== 3) DrawInvisible(te.x, te.y);
              }
            }
          }
        }
        els = els.n;
      }
    }
  }

  let b = bullet_stack;
  while (b !== null) {
    if (!b.delete_me) {
      if (b.room === pr) DrawBullet(b);
    }
    b = b.next;
  }
}

export function MoveEntities(): void {
  const pr = G.player_room;
  if (rooms[pr].room_type !== 3 && rooms[pr].room_type !== 2) {
    let g = room_gems[pr];
    while (g !== null) {
      if (g.room === pr && g.delete_me === 0) {
        if (G.artifacts[7]) {
          g.x += tr((G.player_x + S(4) - g.x) / 10);
          g.y += tr((G.player_y + S(12) - g.y) / 10);
        }
        if (PlayerDist(g.x, g.y) < S(20)) {
          g.delete_me = 1;
          G.total_gems--;
          if (g.value === 31337) {
            if (G.player_hp < 3 + (G.player_shield === 30 ? 3 : 0)) {
              SND_Pos('dat/a/crystal.wav', 64, 0);
              G.player_hp++;
            } else if (!G.training) {
              SND_Pos('dat/a/tone.wav', 64, 0);
              if (G.player_lives === 1) G.player_lives_part += 15;
              else if (G.player_lives < 10) G.player_lives_part += 4;
              else G.player_lives_part += 1;
              if (G.player_lives_part >= 88) {
                G.player_lives_part -= 88;
                G.player_lives += 1;
                SND_Pos('dat/a/crystal2.wav', 100, 0);
              }
            }
          } else {
            G.player_gems += g.value;
          }
        }
      }
      g = g.next_in_room;
    }
  }

  let t = active_stack;
  while (t !== null) {
    if (!t.delete_me) {
      if (rooms[t.room].room_type !== 3 || t.room === pr) MoveEnemy(t);
    }
    t = t.next_active;
  }

  let b = bullet_stack;
  while (b !== null) {
    if (!b.delete_me) MoveBullet(b);
    b = b.next;
  }

  // delete old bullets
  b = bullet_stack;
  if (b !== null && b.delete_me) {
    bullet_stack = b.next;
    b = bullet_stack;
  }
  while (b !== null) {
    if (b.next !== null && b.next.delete_me) {
      b.next = b.next.next;
    }
    b = b.next;
  }

  // delete old gems
  let g = gem_stack;
  const unlinkRoom = (gm: Diamond) => {
    if (gm === room_gems[gm.room]) {
      room_gems[gm.room] = gm.next_in_room;
      if (room_gems[gm.room] !== null) room_gems[gm.room]!.prv_in_room = null;
    } else {
      if (gm.prv_in_room !== null) gm.prv_in_room.next_in_room = gm.next_in_room;
      if (gm.next_in_room !== null) gm.next_in_room.prv_in_room = gm.prv_in_room;
    }
  };
  if (g !== null && g.delete_me) {
    unlinkRoom(g);
    gem_stack = g.next;
    g = gem_stack;
  }
  while (g !== null) {
    if (g.next !== null && g.next.delete_me) {
      unlinkRoom(g.next);
      g.next = g.next.next;
    }
    g = g.next;
  }

  // delete old monsters (from active list)
  t = active_stack;
  if (t !== null) {
    while (t.delete_me) {
      t = t.next_active;
      active_stack = t;
      if (t === null) break;
    }
  }
  while (t !== null) {
    if (t.next_active !== null) {
      while (t.next_active.delete_me) {
        t.next_active = t.next_active.next_active;
        if (t.next_active === null) break;
      }
    }
    t = t.next_active;
  }
}

export function HurtEnemies(x: number, y: number, range: number, power: number): void {
  let t = active_stack;
  while (t !== null) {
    const e_range = tr(Math.sqrt(sqr(t.x - x) + sqr(t.y - y)));
    if (e_range < range) {
      if (power >= t.str) {
        if (t.room === GetRoom(tr(x / TILE), tr(y / TILE))) KillEnemy(t);
      }
    }
    t = t.next_active;
  }
  hurtDataEntities(getActiveWorld(), x, y, range, power);
}

export function CircuitBullets(x: number, y: number, r: number): void {
  let b = bullet_stack;
  while (b !== null) {
    if (!b.delete_me) {
      if (b.dying === 0) {
        if (b.invuln === 0) {
          if (Math.sqrt(sqr(b.x - x) + sqr(b.y - y)) < r) b.dying = 1;
        }
      }
    }
    b = b.next;
  }
}

export function CrystalSummon(): void {
  let g = gem_stack;
  for (let i = 0; i < 3000; i++) room_gems[i] = null;

  while (g !== null) {
    if (!g.delete_me) {
      if (rooms[g.room].room_type !== 3) {
        g.room = G.player_room;
        let rg_x: number;
        let rg_y: number;
        const setPos = () => {
          rg_x = rooms[G.player_room].x * TILE + TILE + (rand() % (rooms[G.player_room].w * TILE - TILE * 2));
          rg_y = rooms[G.player_room].y * TILE + TILE + (rand() % (rooms[G.player_room].h * TILE - TILE * 2));
          if (G.player_room === 0) {
            rg_x = (rooms[G.player_room].x + 5) * TILE + TILE + (rand() % (8 * TILE));
            rg_y = (rooms[G.player_room].y + 5) * TILE + TILE + (rand() % (5 * TILE));
          }
        };
        setPos();
        while (IsSolid(Get(tr(rg_x! / TILE), tr(rg_y! / TILE)))) setPos();
        g.x = rg_x!;
        g.y = rg_y!;
      }
    }
    g.next_in_room = room_gems[g.room];
    g.prv_in_room = null;
    if (room_gems[g.room] !== null) room_gems[g.room]!.prv_in_room = g;
    room_gems[g.room] = g;
    g = g.next;
  }
}

const activateRandPool: Enemy[] = [];

export function ActivateRand(): void {
  activateRandPool.length = 0;
  let e = enemy_stack;
  while (e !== null) {
    if (e.delete_me === 0 && e.active === 0) activateRandPool.push(e);
    e = e.next;
  }
  if (activateRandPool.length > 0) XActivateSingleEnemy(activateRandPool[rand() % activateRandPool.length]);
}

export function ClearBossBullets(): void {
  let b = bullet_stack;
  while (b !== null) {
    if (!b.delete_me) {
      if (b.firer === null) b.delete_me = 1;
    }
    b = b.next;
  }
}

export function SpawnBullet(
  x: number,
  y: number,
  bullet_type: number,
  dir: number,
  spd: number,
  invuln: number
): void {
  if (G.current_boss === 3 && G.player_shield === 30 && G.boss_fight_mode === 2) spd *= 1.2;
  const b = CreateBullet(x, y, null, bullet_type, dir, spd);
  if (invuln) b.invuln = 1;
}

export function SpawnLaser(
  x: number,
  y: number,
  dir: number,
  fire_time: number,
  duration: number,
  turn: number,
  dmg: number
): void {
  FireLaser(x, y, null, dir, fire_time, duration, turn, dmg);
}

export function CullEnemies(nth: number): void {
  let i = 0;
  let e = enemy_stack;
  while (e !== null) {
    if (e.delete_me === 0) {
      if (rooms[e.room].room_type === 0) {
        if (i % nth === nth - 1) {
          e.delete_me = 1;
          G.killed_enemies++;
          e.dying = 0;
          rooms[e.room].enemies--;
        }
        i++;
      }
    }
    e = e.next;
  }
}

export function SoupUpEnemies(): void {
  let str_limit = 1500;
  if (G.circuit_size > 1500) str_limit = 1500;
  const str_multiplier = 1.0 + (1.0 / 3.0) * G.current_boss;
  const fr_divider = 1.0 + (2.0 / 3.0) * G.current_boss;

  let e = enemy_stack;
  while (e !== null) {
    if (e.delete_me === 0) {
      if (e.enemy_type !== 10) {
        if (e.str < str_limit) {
          if (e.str * 2 < str_limit) e.str = tr(e.str * str_multiplier);
          else e.str = str_limit;
        }
        e.fire_rate = tr(e.fire_rate / fr_divider) + 1;
        e.speed = tr(e.speed / fr_divider) + 1;
        e.min_gems = tr(e.min_gems * str_multiplier);
        e.max_gems = tr(e.max_gems * str_multiplier);
      }
    }
    e = e.next;
  }
}

let curseActiveRooms: number[] | null = null;
function CurseSingleEnemy(e: Enemy): void {
  if (curseActiveRooms === null) {
    curseActiveRooms = [];
    for (let i = 0; i < 3000; i++) {
      if (rooms[i].room_type === 0 || rooms[i].room_type === 4) curseActiveRooms.push(i);
    }
  }
  let rm = curseActiveRooms[rand() % curseActiveRooms.length];
  while (rooms[rm].enemies > 3 || rooms[rm].visited === 0) {
    rm = curseActiveRooms[rand() % curseActiveRooms.length];
  }
  e.x = rooms[rm].w * S(16) + rooms[rm].x * TILE;
  e.y = rooms[rm].h * S(16) + rooms[rm].y * TILE;
  rooms[e.room].enemies--;
  e.room = rm;
  rooms[e.room].enemies++;
  e.image = enemy_sprites[9];
  e.lives = 8;
  e.str = 500;
  e.speed = 1;
  e.fire_rate = (rand() % 4) + 1;
  e.min_gems = 5000;
  e.max_gems = 6000;
  e.followdepth = 12;
  e.creationcost = 6;
  e.enemy_type = 10;
  ActivateSingleEnemy(e);
}

export function CurseEnemies(): void {
  curseActiveRooms = null;
  let i = 0;
  let e = enemy_stack;
  while (e !== null) {
    if (e.delete_me === 0) {
      if (i % 5 === 4) {
        CurseSingleEnemy(e);
      } else {
        e.delete_me = 1;
        G.killed_enemies++;
        e.dying = 0;
        rooms[e.room].enemies--;
      }
      i++;
    }
    e = e.next;
  }
}

